import mapKnownValues from "../utils/mapKnownValues";
import TransferSource, { TransferSourceResponse } from "./transferSource";
import TransferDestination, { TransferDestinationResponse } from "./transferDestination";

export enum TransferDisplayOptionValues {
  ira = "IRA",
  employerSponsoredPlan = "EMPLOYER_SPONSORED_PLAN",
  roboIRA = "ROBO_IRA",
  wait = "WAIT",
}

export enum TransferFlow {
  HAS_COMPLETED_HENRY_EXPLORE_SUGGESTIONS = "HAS_COMPLETED_HENRY_EXPLORE_SUGGESTIONS",
  HAS_COMPLETED_HENRY_EXPLORE_EXISTING_DESTINATIONS = "HAS_COMPLETED_HENRY_EXPLORE_EXISTING_DESTINATIONS",
}
export type TransferResponse = {
  destinationAccount: TransferDestinationResponse;
  rothDestinationAccount: TransferDestinationResponse | null;
  sourceAccounts: TransferSourceResponse[] | null;
  transferId: string;
  transferStatus: TransferStatusTypes;
};

export enum TransferSteps {
  connectSource = "CONNECT_SOURCE",
  connectDestination = "CONNECT_DESTINATION",
  initiateTransfer = "INITIATE_TRANSFER",
  trackTransferStatus = "TRACK_TRANSFER_STATUS",
}

export enum TransferStatusTypes {
  none = "",
  cancelled = "CANCELLED",
  complete = "COMPLETE",
  inProgress = "INPROGRESS",
  initiatedAtProvider = "INITIATED_AT_PROVIDER",
  mailProcess = "MAIL_PROCESS",
  processing = "PROCESSING",
  userInputNeeded = "USER_INPUT_NEEDED",
  inQueue = "WAIT_FOR_OPERATIONS",
  waitForIRAOpening = "WAIT_FOR_IRA_OPENING",
}

// The Transfer class is responsible for setting up the transfer and collecting information
// about the source(s) and destination.
// As of right now, we should never see the rothDestinationAccount in the transfer object. This may be
// used in the future.
export default class Transfer {
  destinationAccount: TransferDestination | null = null;
  rothDestinationAccount: TransferDestination | null = null;
  sourceAccounts: TransferSource[] = [];
  transferId = "";
  transferStatus: TransferStatusTypes = TransferStatusTypes.none;

  constructor(params: Partial<Transfer> = {}) {
    mapKnownValues(this, params);
  }

  get currentStep() {
    if (this.sourceAccounts.length === 0) return TransferSteps.connectSource;
    if (this.sourceAccounts.length > 0 && !this.destinationAccount) return TransferSteps.connectDestination;
    if (this.destinationAccount === null) return TransferSteps.connectDestination;
    if (this.destinationAccount && this.destinationAccount.yetToOpen && !this.destinationAccount.isManifestIRAPartner)
      return TransferSteps.connectDestination;
    if (this.transferStatus !== TransferStatusTypes.inProgress) return TransferSteps.trackTransferStatus;
    return TransferSteps.initiateTransfer;
  }

  get currentStepNumber() {
    if (this.currentStep === TransferSteps.connectSource) return 1;
    if (this.currentStep === TransferSteps.connectDestination) return 2;
    if (this.currentStep === TransferSteps.initiateTransfer) return 3;
    return 4;
  }

  // It can only be EMPLOYER_SPONSORED for now
  // eslint-disable-next-line class-methods-use-this
  get sourceRetirementSavingsType() {
    return "EMPLOYER_SPONSORED" as const;
  }

  get sourcePlanSponsorDisplay() {
    const planSponsors = Array.from(new Set(this.getSortedSourceAccounts().map((source) => source.planSponsor)));
    if (planSponsors.length > 1) return planSponsors.join(", ");
    if (planSponsors.length === 1) return planSponsors[0];
    return "";
  }

  get sourceProviderNameDisplay() {
    const providerNames = Array.from(new Set(this.sourceAccounts.map((source) => source.providerNameDisplay)));
    if (providerNames.length > 1) return providerNames.join(", ");
    if (providerNames.length === 1) return providerNames[0];
    return "";
  }

  /**
   * returns the tranfer's source accounts sorted by id in ascending order
   * @returns TransferSource[]
   */
  getSortedSourceAccounts() {
    return this.sourceAccounts.sort((a, b) => {
      if (a.sourceAccountId === -1) return -1;
      if (b.sourceAccountId === -1) return 1;
      return a.sourceAccountId - b.sourceAccountId;
    });
  }

  static fromAPI(response: TransferResponse) {
    let transferSources: TransferSource[] = [];
    if (response.sourceAccounts) {
      transferSources = response.sourceAccounts.map((source) => TransferSource.fromApi(source));
    }
    return new Transfer({
      ...response,
      destinationAccount: response.destinationAccount ? TransferDestination.fromApi(response.destinationAccount) : null,
      rothDestinationAccount: response.rothDestinationAccount
        ? TransferDestination.fromApi(response.rothDestinationAccount)
        : null,
      sourceAccounts: transferSources,
    });
  }
}
